import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  Renderer2,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { catchError, EMPTY, finalize, take, tap } from 'rxjs';

import { AuthService } from 'src/app/core/services/auth';
import { environment } from 'src/environments/environment';

interface DecodedToken {
  exp: number;
  uid: string;
}

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatProgressSpinnerModule,
    ReactiveFormsModule,
    CommonModule,
    RouterLink,
  ],
})
export class ResetPasswordComponent implements OnInit {
  username = signal('');

  token = signal('');

  passwordsMatch = signal(false);

  pwMatchErrorMessage = signal('');

  pwMatchSuccessMessage = signal('');

  criteriaMet = signal(false);

  criteria = ['lower', 'upper', 'special', 'minimum'];

  resetPasswordFormGroup = new FormGroup({
    pw1: new FormControl('', { nonNullable: true }),
    pw2: new FormControl('', { nonNullable: true }),
  });

  displayForm = signal(false);

  isLoading = signal(false);

  errorMessage = signal('');

  siteUrl: string = environment.siteUrl;

  values = {
    lower: {
      label: 'One lowercase character',
      valid: false,
    },
    upper: {
      label: 'One uppercase character',
      valid: false,
    },
    special: {
      label: 'One special character',
      valid: false,
    },
    minimum: {
      label: '8 character minimum',
      valid: false,
    },
  };

  private readonly destroyRef = inject(DestroyRef);

  private router = inject(Router);

  private readonly route = inject(ActivatedRoute);

  private readonly authService = inject(AuthService);

  private readonly renderer = inject(Renderer2);

  private readonly jwtHelper = new JwtHelperService();

  ngOnInit(): void {
    this.token.set(this.route.snapshot.queryParamMap.get('token') ?? '');
    if (!this.token()) {
      return;
    }

    try {
      const decodedToken = this.jwtHelper.decodeToken<DecodedToken>(
        this.token(),
      );
      if (!decodedToken?.exp || !decodedToken?.uid) {
        throw new Error('Invalid token');
        return;
      }

      if (Date.now() >= decodedToken.exp * 1000) {
        return;
      }

      this.username.set(decodedToken.uid);

      this.authService
        .validateResetToken(this.token())
        .pipe(take(1))
        .subscribe((isValid) => {
          this.displayForm.set(isValid);
          if (isValid) this.renderer.selectRootElement('#password').focus();
        });
    } catch (error) {
      this.displayForm.set(false);
    }
  }

  submitResetPassword(): void {
    if (!this.resetPasswordFormGroup.controls.pw2.value.length) {
      this.pwMatchErrorMessage.set('You must reenter your password.');
      this.pwMatchSuccessMessage.set('');
      this.isLoading.set(false);
      return;
    }

    if (!this.passwordsMatch()) {
      this.pwMatchErrorMessage.set(
        'Passwords do not match. Make sure Caps Lock is off.',
      );
      this.pwMatchSuccessMessage.set('');
      this.isLoading.set(false);
      return;
    }
    this.isLoading.set(true);

    this.authService
      .resetPassword(
        this.token(),
        this.resetPasswordFormGroup.controls.pw1.value,
        this.resetPasswordFormGroup.controls.pw2.value,
      )
      .pipe(
        tap((attempt) => {
          this.isLoading.set(false);
          if (attempt === true) {
            // When password changed, redirect to login page
            this.router.navigate(['login'], {
              queryParams: { passwordChanged: true },
            });
          } else {
            this.errorMessage.set('Password reset failed. Please try again.');
          }
        }),
        catchError((err) => {
          this.errorMessage.set(
            (err?.statusText as string) ||
              'Password reset failed. Please try again.',
          );
          this.isLoading.set(false);
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  validatePassword(event: Event): void {
    const input = event.target as HTMLInputElement;
    const pw: string = input.value;

    this.values.lower.valid = /[a-z]/.test(pw);
    this.values.upper.valid = /[A-Z]/.test(pw);
    this.values.special.valid = /\W|_/.test(pw);
    this.values.minimum.valid = /.{8,}/.test(pw);

    this.criteriaMet.set(
      this.values.lower.valid &&
        this.values.upper.valid &&
        this.values.special.valid &&
        this.values.minimum.valid,
    );

    this.doPasswordsMatch();
  }

  doPasswordsMatch(): void {
    this.passwordsMatch.set(
      this.resetPasswordFormGroup.controls.pw1.value ===
        this.resetPasswordFormGroup.controls.pw2.value,
    );
    if (this.passwordsMatch() && this.criteriaMet()) {
      this.pwMatchSuccessMessage.set("Passwords match. You're all set!");
      this.pwMatchErrorMessage.set('');
    } else {
      this.pwMatchSuccessMessage.set('');
      this.pwMatchErrorMessage.set('');
    }
  }
}
